import SearchIcon from '@mui/icons-material/Search';
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Container,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import {useCallback, useState} from 'react';

import PageTitle from '../components/PageTitle';
import Scrollbar from '../components/scrollbar';
import {GeneralLayout} from '../layouts/GeneralLayout';
import {useLazyGetChairWorkloadsQuery} from '../store/employee/employeeApi';

const columns = [
    {key: 'periodRec', label: 'Период'},
    {key: 'yearStart', label: 'Год начала'},
    {key: 'yearEnd', label: 'Год окончания'},
    {key: 'chairCode', label: 'Код кафедры'},
    {key: 'chairName', label: 'Кафедра'},
    {key: 'tchCode', label: 'Код преподавателя'},
    {key: 'tchName', label: 'Преподаватель'},
    {key: 'subjectCode', label: 'Код дисциплины'},
    {key: 'subjectName', label: 'Дисциплина'},
    {key: 'cntrlCode', label: 'Код контроля'},
    {key: 'cntrlName', label: 'Контроль'},
    {key: 'workloadCode', label: 'Код нагрузки'},
    {key: 'workloadName', label: 'Нагрузка'},
    {key: 'workloadRule', label: 'Правило нагрузки'},
    {key: 'contingent', label: 'Контингент'},
    {key: 'hours', label: 'Часы'},
    {key: 'uidSubject', label: 'UID дисциплины'},
    {key: 'uidRow', label: 'UID строки'}
];

function formatPeriod(value) {
    return value ? new Date(value).toLocaleDateString('ru-RU') : '—';
}

function getCellValue(row, key) {
    return key === 'periodRec' ? formatPeriod(row[key]) : row[key] ?? '—';
}

export default function ChairWorkloadsPage() {
    const [filters, setFilters] = useState({
        tchName: '',
        yearStart: '',
        chairName: '',
        contingent: ''
    });
    const [result, setResult] = useState(null);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [loadChairWorkloads, {isFetching}] = useLazyGetChairWorkloadsQuery();

    const load = useCallback(async (page = 1) => {
        const yearStart = filters.yearStart.trim();
        try {
            const response = await loadChairWorkloads({
                tchName: filters.tchName.trim(),
                yearStart: yearStart ? Number(yearStart) : undefined,
                chairName: filters.chairName.trim(),
                contingent: filters.contingent.trim(),
                page
            }).unwrap();
            setResult(response.data);
            setHasLoaded(true);
        } catch (error) {
            setResult(null);
            setHasLoaded(false);
            alert('Не удалось загрузить учебную нагрузку. Повторите попытку позже.');
        }
    }, [filters, loadChairWorkloads]);

    const updateFilter = (name) => (event) => {
        setFilters(current => ({...current, [name]: event.target.value}));
    };

    const handleKeyDown = useCallback((event) => {
        if (event.key === 'Enter') {
            load();
        }
    }, [load]);

    const rows = result?.rows ?? [];

    return <GeneralLayout>
        <PageTitle title="Просмотр учебной нагрузки в 1С"/>

        <Container>
            <Stack direction="column" padding="120px 0 40px 100px" spacing={2}>
                <Box>
                    <Typography variant="h4">Просмотр учебной нагрузки в 1С</Typography>
                </Box>

                <Card>
                    <CardContent>
                        <Stack spacing={2}>
                            <Stack direction={{xs: 'column', md: 'row'}} spacing={2}>
                                <TextField label="ФИО преподавателя" value={filters.tchName}
                                           onChange={updateFilter('tchName')} onKeyDown={handleKeyDown} fullWidth/>
                                <TextField label="Год начала" type="number" value={filters.yearStart}
                                           onChange={updateFilter('yearStart')} onKeyDown={handleKeyDown}
                                           inputProps={{min: 1900, max: 2100}} sx={{minWidth: {md: 160}}}/>
                                <TextField label="Кафедра" value={filters.chairName}
                                           onChange={updateFilter('chairName')} onKeyDown={handleKeyDown} fullWidth/>
                                <TextField label="Контингент" value={filters.contingent}
                                           onChange={updateFilter('contingent')} onKeyDown={handleKeyDown} fullWidth/>
                            </Stack>
                            <Box>
                                <Button variant="contained" onClick={() => load()} disabled={isFetching}
                                        startIcon={isFetching ? <CircularProgress size={18} color="inherit"/> : <SearchIcon/>}>
                                    Загрузить
                                </Button>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>

                {hasLoaded && <Card>
                    {rows.length > 0 && <Scrollbar>
                        <TableContainer sx={{minWidth: 2200}}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        {columns.map(column => <TableCell key={column.key}>{column.label}</TableCell>)}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map(row => <TableRow key={row.uidRow} hover>
                                        {columns.map(column => <TableCell key={column.key}>
                                            {getCellValue(row, column.key)}
                                        </TableCell>)}
                                    </TableRow>)}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Scrollbar>}
                    <Stack direction="row" justifyContent="flex-end" spacing={1} p={2}>
                        <Button onClick={() => load(result.page - 1)} disabled={isFetching || result.page <= 1}>
                            Назад
                        </Button>
                        <Button onClick={() => load(result.page + 1)} disabled={isFetching || !result.hasMore}>
                            Далее
                        </Button>
                    </Stack>
                </Card>}

                {hasLoaded && 
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                            {rows.length ? `${rows.length} строк(и)` : 'Строки не найдены'}
                        </Typography>
                    </Stack>
                }
                
                <br />
            </Stack>
        </Container>
    </GeneralLayout>;
}
